import { useMutation, useQueryClient } from "@tanstack/react-query";
import { orpc } from "@/frontend/orpc-client";
import { getSelectedAccountIds, isFacilityActionable } from "@/frontend/utils/delete-patient-data";
import type { ConfirmAction, FacilityOrganization } from "@/frontend/types/delete-patient-data";
import type { DeletePatientDataActionResult } from "@/server/schema/delete-patient-data";

/**
 * Combines the counts returned by two mutations.
 * @param total Counts accumulated so far.
 * @param next Counts returned by the latest mutation.
 * @returns Combined requested, completed, and failed counts.
 */
function addMutationResults(
  total: DeletePatientDataActionResult,
  next: DeletePatientDataActionResult,
): DeletePatientDataActionResult {
  return {
    requestedCount: total.requestedCount + next.requestedCount,
    completedCount: total.completedCount + next.completedCount,
    failedCount: total.failedCount + next.failedCount,
  };
}

/**
 * Owns the revoke/delete mutations and refreshes their affected queries.
 * @returns Mutation state and a function that changes the selected facilities.
 */
export function useDeletePatientDataMutations(): {
  pending: boolean;
  revokeOrDeleteSelectedFacilities: (
    action: ConfirmAction,
    organizations: FacilityOrganization[],
    selected: ReadonlySet<string>,
  ) => Promise<{
    outcome: DeletePatientDataActionResult;
    remainingSelected: ReadonlySet<string>;
  }>;
} {
  const queryClient = useQueryClient();
  const revokeMutation = useMutation(orpc.deletePatientData.revoke.mutationOptions());
  const deleteMutation = useMutation(orpc.deletePatientData.delete.mutationOptions());
  const revokeOrgMutation = useMutation(orpc.deletePatientData.revokeOrganization.mutationOptions());
  const deleteOrgMutation = useMutation(orpc.deletePatientData.deleteOrganization.mutationOptions());
  const revokeAllMutation = useMutation(orpc.deletePatientData.revokeAll.mutationOptions());
  const deleteAllMutation = useMutation(orpc.deletePatientData.deleteAll.mutationOptions());

  const pending =
    revokeMutation.isPending
    || deleteMutation.isPending
    || revokeOrgMutation.isPending
    || deleteOrgMutation.isPending
    || revokeAllMutation.isPending
    || deleteAllMutation.isPending;

  /** Executes a confirmed selection and returns only selections that still need action. */
  const revokeOrDeleteSelectedFacilities = async (
    action: ConfirmAction,
    organizations: FacilityOrganization[],
    selected: ReadonlySet<string>,
  ): Promise<{
    outcome: DeletePatientDataActionResult;
    remainingSelected: ReadonlySet<string>;
  }> => {
    let outcome: DeletePatientDataActionResult = {
      requestedCount: 0,
      completedCount: 0,
      failedCount: 0,
    };

    /**
     * Runs one mutation and includes thrown failures in the displayed counts.
     * @param facilityCount Number of facilities affected by the mutation.
     * @param mutate Mutation call to run.
     * @returns Nothing after the displayed counts have been updated.
     */
    const runMutation = async (
      facilityCount: number,
      mutate: () => Promise<DeletePatientDataActionResult>,
    ): Promise<void> => {
      try {
        outcome = addMutationResults(outcome, await mutate());
      } catch {
        outcome = addMutationResults(outcome, {
          requestedCount: facilityCount,
          completedCount: 0,
          failedCount: facilityCount,
        });
      }
    };

    if (action.scope === "all") {
      await runMutation(selected.size, () =>
        action.kind === "revoke"
          ? revokeAllMutation.mutateAsync({})
          : deleteAllMutation.mutateAsync({}),
      );
    } else {
      for (const organization of organizations) {
        const selectedAccountIds = getSelectedAccountIds(organization, selected);
        if (selectedAccountIds.length === 0) continue;

        const availableFacilityCount = organization.facilities.filter(isFacilityActionable).length;
        if (selectedAccountIds.length === availableFacilityCount && selectedAccountIds.length > 1) {
          await runMutation(selectedAccountIds.length, () =>
            action.kind === "revoke"
              ? revokeOrgMutation.mutateAsync({ organizationId: organization.organizationId })
              : deleteOrgMutation.mutateAsync({ organizationId: organization.organizationId }),
          );
          continue;
        }

        for (const accountId of selectedAccountIds) {
          await runMutation(1, () =>
            action.kind === "revoke"
              ? revokeMutation.mutateAsync({ accountId })
              : deleteMutation.mutateAsync({ accountId }),
          );
        }
      }
    }

    await queryClient.invalidateQueries({ queryKey: orpc.deletePatientData.listFacilities.key() });
    await queryClient.invalidateQueries({ queryKey: orpc.deletePatientData.session.key() });
    const refreshed = await queryClient.fetchQuery(
      orpc.deletePatientData.listFacilities.queryOptions({ input: {} }),
    );
    const stillPending = new Set(
      refreshed.organizations.flatMap((organization) =>
        organization.facilities
          .filter((facility) =>
            action.kind === "delete"
              ? !facility.deletionScheduledAt
              : !facility.deletionScheduledAt && !facility.revoked,
          )
          .map((facility) => facility.accountId),
      ),
    );
    const remainingSelected = new Set(
      [...selected].filter((accountId) => stillPending.has(accountId)),
    );
    return { outcome, remainingSelected };
  };

  return { pending, revokeOrDeleteSelectedFacilities };
}
